import { ReactNode, useContext, useEffect, useState } from "react";
import { useGeneralContext } from "../GeneralContext/GeneralProvider";
import UserModalContext from "./UserModalContext";
import { DefaultGroup, DefaultNotice } from "../../Interfaces/DefaultEntities";
import { GroupInterface, NoticeInterface, SchoolInterface } from "../../Interfaces/Entities";
import { FetchGetFunction } from "../../Helpers/FetchHelpers";


// Background polling only while the tab is visible. Push notifications remain the primary
// delivery path; this is a cheap fallback so open tabs eventually catch up.
const DEFAULT_NOTICE_POLL_INTERVAL_MS = 3 * 60 * 1000

function readNoticePollIntervalMs(): number {
	const configured = Number(process.env.REACT_APP_NOTICE_POLL_INTERVAL_MS)
	if (Number.isFinite(configured) && configured >= 60_000) {
		return configured
	}
	return DEFAULT_NOTICE_POLL_INTERVAL_MS
}

function UserModalProvider({ children }: { children: ReactNode }) {

	const { user, groupsOfUserAsGroups, schoolOfUser, setCurrentModal, getInfoOfSchool,
		setSchoolOfUser } = useGeneralContext()
	const [savedNotices, setSavedNotices] = useState<NoticeInterface[]>([])
	const [allNotices, setAllNotices] = useState<NoticeInterface[]>([])
	const [unResolvedNotices, setUnResolvedNotices] = useState<NoticeInterface[]>([])
	const [resolvedNotices, setResolvedNotices] = useState<NoticeInterface[]>([])
	const [savedNewsLetters, setSavedNewsLetters] = useState<NoticeInterface[]>([])
	const [isFirstUpdate, setIsFirstUpdate] = useState<boolean>(true)
	const [noticeToSee, setNoticeToSee] = useState<NoticeInterface>(DefaultNotice)
	const [groupToSee, setGroupToSee] = useState<GroupInterface>(DefaultGroup)
	const [isLoadingInfo, setIsLoadingInfo] = useState<boolean>(false)


	const fetchNotices = async (schoolId: string) => {
		try {
			if (!user) {
				return
			}
			await handleNoticeFetch(user.SK, schoolId)
		} catch (error) {
			console.log(error)
		}
	};

	// Función helper para formatear fecha al formato esperado por el backend: "%Y-%m-%d %H:%M:%S"
	const formatDateForBackend = (date: Date, isEndDate: boolean = false): string => {
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		const hours = isEndDate ? '23' : '00';
		const minutes = isEndDate ? '59' : '00';
		const seconds = isEndDate ? '59' : '00';
		return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
	}

	const handleNoticeFetch = async (userId: string, schoolId: string, startDate?: Date, endDate?: Date) => {
		let url = `${process.env.REACT_APP_BACKEND_URL}/news?user_id=${userId}&school_id=${schoolId}`;
		
		// Validar que endDate sea mayor que startDate, si no, establecer endDate a hoy
		let validEndDate = endDate;
		if (startDate && endDate && endDate <= startDate) {
			validEndDate = new Date();
			validEndDate.setHours(23, 59, 59, 999);
		}
		
		// Si se especifican fechas, agregarlas como query params (formato "%Y-%m-%d %H:%M:%S")
		if (startDate) {
			const startStr = formatDateForBackend(startDate, false);
			url += `&start_date=${encodeURIComponent(startStr)}`;
		}
		if (validEndDate) {
			const endStr = formatDateForBackend(validEndDate, true);
			url += `&end_date=${encodeURIComponent(endStr)}`;
		}
		
		const noticeFromBack = await FetchGetFunction(url)
		const editedNotices = noticeFromBack.map((notice: NoticeInterface) => ({
			...notice,
			comments: notice.comments ?? [],
			date: notice.SK.split('.')[0],
			solved: notice.solved ?? false,
		}));
		setAllNotices(editedNotices);
		setIsFirstUpdate(false);
		if (noticeToSee.SK !== DefaultNotice.SK) {
			const currentNoticeToSee = editedNotices.filter((notice: NoticeInterface) => notice.SK === noticeToSee.SK);
			if (currentNoticeToSee) {
				setNoticeToSee(currentNoticeToSee[0]);
			}
		}
	}

	const updateNotices = async () => {
		const schoolId = schoolOfUser ? schoolOfUser.SK : (user?.school || '')
		await fetchNotices(schoolId);
	};

	const updateNoticesWithDateRange = async (startDate: Date, endDate: Date) => {
		if (!user) {
			return
		}
		const schoolId = schoolOfUser ? schoolOfUser.SK : (user?.school || '')
		await handleNoticeFetch(user.SK, schoolId, startDate, endDate)
	};

	const updateNoticesForSpecificSchool = async (school: SchoolInterface, setIsLoading:
		React.Dispatch<React.SetStateAction<boolean>>) => {
		setIsLoading(true)
		await fetchNotices(school.SK);
		setIsLoading(false)
	};


	const updateInternalNotices = (startingNotices: NoticeInterface[]) => {
		const newsLetters = startingNotices.filter((notice: NoticeInterface) => notice.type === 'General')
		const noticesToCheck = startingNotices.filter((notice: NoticeInterface) => notice.type !== 'General')
		const unresolved = noticesToCheck.filter((notice: NoticeInterface) => notice.solved === false)
		const resolved = noticesToCheck.filter((notice: NoticeInterface) => notice.solved === true)
		setSavedNewsLetters(newsLetters)
		setSavedNotices(noticesToCheck)
		setUnResolvedNotices(unresolved)
		setResolvedNotices(resolved)
	}

	const handleGroupSelection = async (groupId: string) => {
		const filteredGroup = groupsOfUserAsGroups.filter((group: GroupInterface) => group.SK === groupId)
		if (filteredGroup.length === 1) {
			setGroupToSee(filteredGroup[0])
			setCurrentModal('SeeGroup', groupId)
		}
	}

	const handleLoadingInfo = async () => {
		if (!isFirstUpdate || !user || !user.school) {
			return
		}
		setIsLoadingInfo(true)
		// Groups are loaded by the provider once the school lands, so they are not requested here.
		await Promise.all([
			getInfoOfSchool(user.school),
			fetchNotices(user.school),
		])
		setIsFirstUpdate(false)
		setIsLoadingInfo(false)
	}

	const handleAdminEnter = async ( school : SchoolInterface, schoolId : string ) => {
		setSchoolOfUser(school)
		await fetchNotices(schoolId)
	}

	const handleUpdateNotices = async () => {
		if (!user) {
			return
		}
		if (user.admin === 'SuperAdmin' && schoolOfUser) {
			await fetchNotices(schoolOfUser.SK)
			return
		}
		await fetchNotices(user.school)
	}

	// The page can open on any section after a reload or from a link, so the school is loaded
	// here instead of by Entrada. A super admin has no school until the URL picks one.
	useEffect(() => {
		if (user && user.admin !== 'SuperAdmin') {
			handleLoadingInfo()
		}
		// handleLoadingInfo is rebuilt on every render and guards itself against running twice.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (!isFirstUpdate && savedNotices.length === 0) {
			updateNotices()
		}

		const refreshNoticesIfVisible = () => {
			if (document.visibilityState === 'visible') {
				handleUpdateNotices()
			}
		}

		const handleVisibilityChange = () => {
			refreshNoticesIfVisible()
		}

		const intervalId = setInterval(refreshNoticesIfVisible, readNoticePollIntervalMs())

		document.addEventListener('visibilitychange', handleVisibilityChange)

		return () => {
			document.removeEventListener('visibilitychange', handleVisibilityChange)
			clearInterval(intervalId)
		}
	}, [schoolOfUser])

	useEffect(() => {
		updateInternalNotices(allNotices)
	}, [allNotices])

	return (
		<UserModalContext.Provider value={{
			savedNotices, setSavedNotices, updateNotices, updateNoticesWithDateRange, unResolvedNotices, setUnResolvedNotices, resolvedNotices,
			setResolvedNotices, savedNewsLetters, setSavedNewsLetters, isFirstUpdate, setIsFirstUpdate, allNotices,
			setAllNotices, noticeToSee, setNoticeToSee, groupToSee, setGroupToSee, updateNoticesForSpecificSchool, 
			isLoadingInfo, handleGroupSelection, handleAdminEnter
		}}>
			{children}
		</UserModalContext.Provider>
	)
}

function useUserModalContext() {
	const context = useContext(UserModalContext);
	if (!context) {
		throw new Error('useModal must be used within a ModalProvider');
	}
	return context
}

export { useUserModalContext }
export default UserModalProvider
